import { sortRowsArray, } from "@tedivo/tedivo-bay-grid-pure";
import { createInputWithUnits } from "@tedivo/tedivo-form";
import { isValidHeight, isValidTiers } from "./lashingBridgesEditModel";
import { getTranslation } from "../../../../../../../app/i18/i18tn";
import globalUnits from "../../../../../../../app/units/globalUnits";
/**
 * Opens a modal to edit the per-row exceptions of one side of a lashing bridge.
 * Lists every row of the bay plus the existing exceptions for rows the bay doesn't have.
 * It works on a copy: nothing changes until Apply, which calls `onApply` with the new `perRow`.
 */
export function openPerRowExceptionsDialog({ parent, isoBay, side, rows, perRow, defaults, usingAModal, onApply, }) {
    const dialog = document.createElement("sl-dialog");
    dialog.classList.add("higher-modal-z-index");
    dialog.setAttribute("style", "--width: 640px");
    dialog.label = getTranslation("view:edit.lashingBridges.dialogTitle", {
        bay: isoBay,
        side: getTranslation(`enums:ForeAftEnum.${side === "fore" ? "FWD" : "AFT"}`),
    });
    const help = document.createElement("p");
    help.innerHTML = getTranslation("view:edit.lashingBridges.dialogHelp");
    dialog.appendChild(help);
    const outsideRows = Object.keys(perRow)
        .filter((r) => !rows.includes(r))
        .sort(sortRowsArray);
    const table = document.createElement("table");
    table.className = "tvd-table tvd-table-sticky";
    const trh = document.createElement("tr");
    [
        "general:grid.row",
        "view:edit.lashingBridges.override",
        "view:edit.lashingBridges.tiers",
        "view:edit.lashingBridges.height",
        "",
    ].forEach((key) => {
        const th = document.createElement("th");
        th.innerHTML = key ? getTranslation(key) : "";
        trh.appendChild(th);
    });
    const tHead = document.createElement("thead");
    tHead.appendChild(trh);
    const tBody = document.createElement("tbody");
    const controls = [...rows, ...outsideRows].map((row) => {
        const c = createRow(row, outsideRows.includes(row));
        tBody.appendChild(c.tr);
        return c.controls;
    });
    table.appendChild(tHead);
    table.appendChild(tBody);
    dialog.appendChild(table);
    const btnCancel = document.createElement("sl-button");
    btnCancel.slot = "footer";
    btnCancel.innerHTML = getTranslation("general:common.cancel");
    btnCancel.addEventListener("click", () => dialog.hide());
    const btnApply = document.createElement("sl-button");
    btnApply.slot = "footer";
    btnApply.variant = "primary";
    btnApply.innerHTML = getTranslation("general:common.accept");
    btnApply.addEventListener("click", apply);
    dialog.appendChild(btnCancel);
    dialog.appendChild(btnApply);
    dialog.addEventListener("sl-request-close", (ev) => {
        if (ev.target === dialog && ev.detail.source === "overlay")
            ev.preventDefault();
    });
    dialog.addEventListener("sl-after-hide", (ev) => {
        if (ev.target !== dialog)
            return;
        usingAModal === null || usingAModal === void 0 ? void 0 : usingAModal(false);
        dialog.remove();
    });
    parent.appendChild(dialog);
    usingAModal === null || usingAModal === void 0 ? void 0 : usingAModal(true);
    dialog.show();
    function createRow(row, isOutside) {
        const current = perRow[row];
        const tr = document.createElement("tr");
        const tdRow = document.createElement("td");
        tdRow.innerHTML = row;
        if (isOutside) {
            const badge = document.createElement("sl-badge");
            badge.variant = "warning";
            badge.innerHTML = getTranslation("view:edit.lashingBridges.outsideBay");
            badge.style.marginLeft = "0.5em";
            tdRow.appendChild(badge);
        }
        tr.appendChild(tdRow);
        const tdOverride = document.createElement("td");
        tdOverride.className = "centered";
        const override = document.createElement("sl-switch");
        override.checked = !!current;
        tdOverride.appendChild(override);
        tr.appendChild(tdOverride);
        const tdTiers = document.createElement("td");
        const tiers = document.createElement("sl-input");
        tiers.type = "number";
        tiers.size = "small";
        tiers.min = 0;
        tiers.step = 1;
        tiers.value = current ? String(current.tiers) : "";
        tiers.placeholder = defaults.tiers !== undefined ? String(defaults.tiers) : "";
        tdTiers.appendChild(tiers);
        tr.appendChild(tdTiers);
        const tdHeight = document.createElement("td");
        const height = createInputWithUnits({
            name: `${row}.height`,
            value: current === null || current === void 0 ? void 0 : current.height,
            placeholder: current ? undefined : defaults.height,
            converter: globalUnits.lengthUnits,
        });
        tdHeight.appendChild(height);
        tr.appendChild(tdHeight);
        const tdNoBridge = document.createElement("td");
        const btnNoBridge = document.createElement("sl-button");
        btnNoBridge.size = "small";
        btnNoBridge.innerHTML = getTranslation("view:edit.lashingBridges.noBridge");
        btnNoBridge.addEventListener("click", () => {
            override.checked = true;
            tiers.value = "0";
            height.value = undefined;
            enable();
        });
        tdNoBridge.appendChild(btnNoBridge);
        tr.appendChild(tdNoBridge);
        const enable = () => {
            var _a;
            tiers.disabled = !override.checked;
            height.disabled = !override.checked;
            if (override.checked && tiers.value === "")
                tiers.value = String((_a = defaults.tiers) !== null && _a !== void 0 ? _a : 0);
        };
        override.addEventListener("sl-change", enable);
        enable();
        return { tr, controls: { row, override, tiers, height } };
    }
    function apply() {
        const newPerRow = {};
        let valid = true;
        controls.forEach(({ row, override, tiers, height }) => {
            tiers.classList.remove("has-error");
            height.classList.remove("has-error");
            if (!override.checked)
                return;
            const t = tiers.value === "" ? undefined : Number(tiers.value);
            const h = height.value === undefined || height.value === null ? undefined : Number(height.value);
            if (!isValidTiers(t)) {
                tiers.classList.add("has-error");
                valid = false;
            }
            if (!isValidHeight(h)) {
                height.classList.add("has-error");
                valid = false;
            }
            if (isValidTiers(t))
                newPerRow[row] = h !== undefined ? { tiers: t, height: h } : { tiers: t };
        });
        if (!valid)
            return;
        onApply(newPerRow);
        dialog.hide();
    }
}
